import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import { jsPDF } from 'jspdf';

export class FormDownloadService {
	private dateStamp(): string {
		const d = new Date();
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
	}

	// ─── Content/Artifact Downloads ─────────────────────────────────

	async downloadContentAsWord(title: string, content: string): Promise<void> {
		const lines = content.split('\n');
		const children: Paragraph[] = [
			new Paragraph({
				children: [new TextRun({ text: title, bold: true, size: 28 })],
				heading: HeadingLevel.HEADING_1,
			}),
			new Paragraph({
				children: [new TextRun({ text: `Downloaded: ${new Date().toLocaleDateString()}`, size: 20, color: '666666' })],
			}),
			new Paragraph({ text: '' }),
		];

		for (const line of lines) {
			if (line.startsWith('### ')) {
				children.push(new Paragraph({ children: [new TextRun({ text: line.slice(4), bold: true, size: 22 })], heading: HeadingLevel.HEADING_3 }));
			} else if (line.startsWith('## ')) {
				children.push(new Paragraph({ children: [new TextRun({ text: line.slice(3), bold: true, size: 24 })], heading: HeadingLevel.HEADING_2 }));
			} else if (line.startsWith('# ')) {
				children.push(new Paragraph({ children: [new TextRun({ text: line.slice(2), bold: true, size: 26 })], heading: HeadingLevel.HEADING_1 }));
			} else if (line.startsWith('- ') || line.startsWith('* ')) {
				children.push(new Paragraph({ children: [new TextRun({ text: `• ${line.slice(2)}`, size: 22 })] }));
			} else if (/^\d+\.\s/.test(line)) {
				children.push(new Paragraph({ children: [new TextRun({ text: line, size: 22 })] }));
			} else if (line.trim() === '') {
				children.push(new Paragraph({ text: '' }));
			} else {
				// Strip bold markers for Word
				const cleaned = line.replace(/\*\*(.*?)\*\*/g, '$1');
				children.push(new Paragraph({ children: [new TextRun({ text: cleaned, size: 22 })] }));
			}
		}

		const doc = new Document({ sections: [{ children }] });
		const blob = await Packer.toBlob(doc);
		this.triggerDownload(blob, `${title}-${this.dateStamp()}.docx`);
	}

	downloadContentAsPdf(title: string, content: string): void {
		const pdf = new jsPDF();
		let y = 20;
		const leftMargin = 20;
		const maxWidth = pdf.internal.pageSize.getWidth() - leftMargin * 2;

		const ensureSpace = (needed: number): void => {
			if (y + needed > pdf.internal.pageSize.getHeight() - 20) {
				pdf.addPage();
				y = 20;
			}
		};

		pdf.setFontSize(18);
		pdf.setFont('helvetica', 'bold');
		pdf.text(title, leftMargin, y);
		y += 8;
		pdf.setFontSize(10);
		pdf.setFont('helvetica', 'normal');
		pdf.setTextColor(100);
		pdf.text(`Downloaded: ${new Date().toLocaleDateString()}`, leftMargin, y);
		pdf.setTextColor(0);
		y += 10;

		const lines = content.split('\n');
		for (const line of lines) {
			if (line.startsWith('### ')) {
				ensureSpace(12);
				pdf.setFontSize(13);
				pdf.setFont('helvetica', 'bold');
				pdf.text(line.slice(4), leftMargin, y);
				y += 7;
			} else if (line.startsWith('## ')) {
				ensureSpace(14);
				pdf.setFontSize(14);
				pdf.setFont('helvetica', 'bold');
				pdf.text(line.slice(3), leftMargin, y);
				y += 8;
			} else if (line.startsWith('# ')) {
				ensureSpace(16);
				pdf.setFontSize(16);
				pdf.setFont('helvetica', 'bold');
				pdf.text(line.slice(2), leftMargin, y);
				y += 9;
			} else if (line.trim() === '') {
				y += 4;
			} else {
				ensureSpace(8);
				pdf.setFontSize(11);
				pdf.setFont('helvetica', 'normal');
				const cleaned = line.replace(/\*\*(.*?)\*\*/g, '$1').replace(/^[-*]\s/, '• ');
				const wrapped = pdf.splitTextToSize(cleaned, maxWidth);
				pdf.text(wrapped, leftMargin, y);
				y += wrapped.length * 5 + 2;
			}
		}

		pdf.save(`${title}-${this.dateStamp()}.pdf`);
	}

	private triggerDownload(blob: Blob, filename: string): void {
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}
}
